const quoteUtils = require('./utils')
const resourcePrice = require('../resource/price')
const info = require('../resource/info')

// we assume that we are allowed to mutate these values
const QuoteModel = ({
  items,
  children,
  expanded,
  preRounding = false,
  ignoreRates = false,
  mergeAdjustments = false,
  markups = null,
  onAddMarkup = () => {},
  expandAll = false,
}) => {

  const detatchItem = (id) => {
    const item = items[id]

    // remove the child from it's parent children
    children[item.parent] = (children[item.parent] || []).filter(i => i != id)

    // reset the 'parent'
    item.parent = ''    
  }

  const attachItem = (id, parentId, index = -1) => {
    const item = items[id]
    item.parent = parentId
    if(index < 0) {
      children[parentId] = (children[parentId] || []).concat([id])
    }
    else {
      const currentChildren = (children[parentId] || []).map(i => i)
      currentChildren.splice(index, 0, id)
      children[parentId] = currentChildren
    }
  }

  const moveItem = (id, parentId, index = -1) => {
    detatchItem(id)
    attachItem(id, parentId, index)
  }

  const expandTopFolders = () => {
    Object.keys(expanded).forEach(id => expanded[id] = false)
    const rootChildren = children.root || []
    rootChildren.forEach(id => {
      const item = items[id]
      if(item.type == 'folder') expanded[id] = true
    })
  }

  const addItem = ({
    parent,
    item,
  }) => {
    items[item.id] = item

    if(parent == 'root') {

      const parentChildren = children[parent] || []
      
      if(item.type == 'folder') {

        // add the folder and slurp up trailing items
        const trailingIds = quoteUtils.getTrailingItems({
          items: items,
          children: parentChildren,
        })

        trailingIds.forEach(trailingId => moveItem(trailingId, item.id))
        attachItem(item.id, parent)

        expanded[item.id] = true
      }
      else {
        const lastFolderId = quoteUtils.getLastFolder({
          items: items,
          children: parentChildren,
        })

        if(!lastFolderId) {
          attachItem(item.id, parent)
        }
        else {
          attachItem(item.id, lastFolderId)
          expanded[lastFolderId] = true
        }
      }
    }
    // if we are adding to a folder just append the item
    // to the end of the folder list
    else {
      attachItem(item.id, parent)
      expanded[parent] = true
    }
  }

  const handleDrag = (payload) => {

    const dragItem = items[payload.dragItem.id]
    const dropItem = items[payload.dropItem.id]

    if(dragItem.type == 'folder') {

      // we are just swapping folders is all
      if(dropItem.type == 'folder') {
        const parentChildren = children[dragItem.parent] || []
        const dropIndex = parentChildren.indexOf(dropItem.id)
        moveItem(dragItem.id, dragItem.parent, dropIndex)
      }
      // dragging a folder downwards into it's contents
      // move the contents to the folder above (or root)
      else if (dropItem.parent == dragItem.id) {
        const parentChildren = children[dragItem.parent] || []
        const parentIndex = parentChildren.indexOf(dragItem.id)
        const beforeParent = parentChildren.slice(0, parentIndex)
        const lastFolderId = beforeParent.reverse().find(id => items[id].type == 'folder')

        // move to the closest folder above
        if(lastFolderId) {
          moveItem(dropItem.id, lastFolderId)
        }
        // move to the very top just above the current folder
        else {
          moveItem(dropItem.id, 'root', parentIndex)
        }
      }
      // dragging upwards onto items either in the root or another folder
      else {
        moveItem(dropItem.id, dragItem.id, 0)
      }
    }
    else {
      // the drag is within a single folder
      if(dragItem.parent == dropItem.parent && dragItem.type != 'folder' && dropItem.type != 'folder') {
        const parentChildren = children[dragItem.parent] || []
        const fromIndex = parentChildren.indexOf(dragItem.id)
        const toIndex = parentChildren.indexOf(dropItem.id)
        parentChildren[fromIndex] = dropItem.id
        parentChildren[toIndex] = dragItem.id
        children[dragItem.parent] = parentChildren
      }
      // dragging upwards and we have hit the parent folder
      // let's find the folder before this one and add the item to the end
      else if(dragItem.parent == dropItem.id) {
        const parent = items[dragItem.parent]
        const parentChildren = children[parent.parent] || []
        const parentIndex = parentChildren.indexOf(dragItem.parent)
        const beforeParent = parentChildren.slice(0, parentIndex)
        const lastFolderId = beforeParent.reverse().find(id => items[id].type == 'folder')

        // move to the closest folder above
        if(lastFolderId) {
          moveItem(dragItem.id, lastFolderId)
        }
        // move to the very top just above the current folder
        else {
          moveItem(dragItem.id, 'root', parentIndex)
        }
      }
      // dragging downwards onto the next folder
      else {
        const parent = items[dropItem.id]
        // this shouldn't happen
        if(parent.type != 'folder') throw new Error(`a drop target was not a folder`)
        moveItem(dragItem.id, parent.id, 0)
      }
      
    }
    
  }

  const isItemExpanded = (item) => {
    if(expandAll) return true
    return expanded[item.id] ? true : false
  }

  const getItemChildren = ({
    parentId,
  }) => {
    const childIds = children[parentId] || []
    const childItems = childIds.map(id => items[id])
    return childItems
  }
  
  const getChildLinks = ({
    item,
    depth,
    price,
  }) => {
    if(!info.isLinkBased(item.resource)) return []
    const allLinkInputs = item.linkInputs || []
    const allLinkEdits = item.linkEdits || []
    return item.resource.links.map((link, i) => {
      const linkPrice = price.links[i]
      const priceMode = linkPrice.link.meta.mode
      onAddMarkup({
        type: link.resource.type,
        price: linkPrice.price,
      })
      return {
        item: {
          type: 'link',
          resource: link.resource,
          id: item.id,
          parent: item.id,
          inputs: allLinkInputs[i] || {},
          edits: allLinkEdits[i] || {},
        },
        depth,
        group: `link-${item.id}`,
        linkIndex: i,
        isLink: true,
        hasChildren: false,
        price: linkPrice,
        basePrice: price,
        priceMode,
      }
    })
  }

  // get a table for the children of an item based on the expanded state
  const getItemContents = ({
    item,
    depth,
    index,
  }) => {
    const isExpanded = isItemExpanded(item)
    const inputs = item.inputs || {}
    const linkInputs = item.linkInputs || []
    const amount = parseFloat(inputs.amount || 0)
    const overrides = {
      links: linkInputs,
      ...inputs,
    }
    const price = item.type == 'task' ? resourcePrice({
      resource: item.resource,
      amount,
      overrides,
      markups,
      preRounding,
      ignoreRates,
      mergeAdjustments,
    }) : null
    if(price) {
      onAddMarkup({
        type: item.resource.type,
        price: price.price,
      })
    }
    const row = {
      item,
      depth,
      group: `item-${item.parent}-${item.type}`,
      folderIndex: index,
      hasChildren: info.hasChildren(item.resource),
      expanded: isExpanded,
      price,
    }
    const childLinks = getChildLinks({
      item,
      depth: depth+1,
      price,
    })
    
    const childRows = getItemChildren({
      parentId: item.id
    }).reduce((all, child, j) => {
      return all.concat(getItemContents({
        item: child,
        depth: depth+1,
        index: j,
      }))
    }, [])

    // work out the price for folders

    if(item.type == 'folder') {
      const totalPrice = childRows
        .filter(row => {
          return row.item.parent == item.id
        })
        .reduce((total, row) => {
          return total + row.price.price
        }, 0)
      row.price = {
        price: totalPrice,
      }
    }

    return isExpanded ?
      [row].concat(childLinks).concat(childRows) :
      [row]
  }

  const mergeRows = ({
    filter = (item) => true,
  }) => {
    const rootChildren = children.root || []
    const allRows = rootChildren
      .reduce((all, id, i) => {      
        return all.concat(getItemContents({
          item: items[id],
          depth: 0,
          index: i,
        }))
      }, [])
      .filter(row => filter(row.item))
    return allRows
  }

  return {
    detatchItem,
    attachItem,
    moveItem,
    expandTopFolders,
    addItem,
    handleDrag,
    getItemChildren,
    getChildLinks,
    isItemExpanded,
    mergeRows,
    getItemContents,
  }
}

module.exports = QuoteModel